import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link, MapPin, Euro, Plus, X } from "lucide-react";
import { TransportationActivity } from "../models/activity.model";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import { DayField } from "../components/DayField";
import type { DayAxis } from "../models/day-axis";
import { useI18n } from "@/hooks/useI18n";
import ActivityModalShell from "./ActivityModalShell";
import ActivityDetailView from "./ActivityDetailView";

type TransportationTab = 'details' | 'documents';

interface TransportationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<TransportationActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<TransportationActivity>;
  itineraryId: string;
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
  /** Asse dei giorni: sui template ogni tappa si colloca su "Giorno N". */
  axis?: DayAxis | null;
  defaultDate?: string;
  /** Sola lettura: campi non interagibili, footer con solo "Chiudi" (usato dalla Libreria). */
  readOnly?: boolean;
  /** Il cliente ha gia' pagato: costo e "includi nel prezzo" sono consolidati. */
  pricingLocked?: boolean;
}

const TransportationModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryId, itineraryStartDate, itineraryEndDate, axis, defaultDate, readOnly = false, pricingLocked = false }: TransportationModalProps) => {
  const { t } = useI18n();

  // Generate temporary UUID for new activities
  const [activityId] = useState<string>(() =>
    initialData?.id || crypto.randomUUID()
  );

  const [formData, setFormData] = useState<Partial<TransportationActivity>>({
    id: activityId,
    include_price: true,
    title: '',
    link: '',
    transport_type: 'train',
    company: '',
    travels: [
      { address: '', date: undefined, time: '' },
      { address: '', date: undefined, time: '' }
    ],
    cost: 0,
    description: '',
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData, id: activityId });
    }
  }, [initialData, activityId]);

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [activeTab, setActiveTab] = useState<TransportationTab>('details');

  useEffect(() => {
    if (!isOpen) {
      setShowValidationBanner(false);
      setErrors({});
      setActiveTab('details');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !initialData && defaultDate) {
      setFormData(prev => ({
        ...prev,
        travels: prev.travels?.map(t => ({ ...t, date: defaultDate })) || prev.travels
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const addTravel = (index: number) => {
    setFormData(prev => ({
      ...prev,
      travels: [
        ...(prev.travels || []).slice(0, index + 1),
        { address: '', date: '', time: '' },
        ...(prev.travels || []).slice(index + 1)
      ]
    }));
  };

  const removeTravel = (index: number) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.filter((_, i) => i !== index) || []
    }));
  };

  const updateTravel = (index: number, field: keyof TransportationActivity['travels'][0], value: string | number) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.map((travel, i) =>
        i === index ? { ...travel, [field]: value } : travel
      ) || []
    }));
  };

  const validateForm = (): Record<string, boolean> => {
    const requiredFields = ['title', 'company', 'cost'];
    const newErrors: Record<string, boolean> = {};

    requiredFields.forEach(field => {
      const value = formData[field as keyof TransportationActivity];
      if (field === 'cost') {
        newErrors[field] = value === null || value === undefined || (value as number) < 0;
      } else {
        newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
      }
    });

    if (!formData.travels || formData.travels.length === 0) {
      newErrors.travels = true;
    } else {
      formData.travels.forEach((travel, index) => {
        const travelRequiredFields = ['address', 'date', 'time'];
        travelRequiredFields.forEach(field => {
          const value = travel[field as keyof typeof travel];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors[`travel_${index}_${field}`] = true;
          }
        });
      });
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (!Object.values(newErrors).some(error => error)) {
      onSave({
        ...formData,
        type: 'transportation' as const,
        date: formData.travels ? formData.travels[0].date : undefined
      });
      onClose();
      return;
    }
    setShowValidationBanner(true);
    setActiveTab('details');
  };

  const transportTypeKeys: Record<string, string> = {
    train: 'modals.transportTypes.train',
    bus: 'modals.transportTypes.bus',
    car: 'modals.transportTypes.car',
    taxi: 'modals.transportTypes.taxi',
    metro: 'modals.transportTypes.metro',
    other: 'modals.transportTypes.other',
  };

  const hasDetailsError = Object.keys(errors).some(key => errors[key]);

  if (readOnly) {
    return (
      <ActivityDetailView
        isOpen={isOpen}
        onClose={onClose}
        icon={icon}
        title={t('builder.activities.transportation')}
        activity={formData}
      />
    );
  }

  return (
    <ActivityModalShell
      isOpen={isOpen}
      onClose={onClose}
      icon={icon}
      title={t('builder.activities.transportation')}
      formId="transportation-form"
      onSubmit={handleSubmit}
      showValidationBanner={showValidationBanner}
      activeTab={activeTab}
      onActiveTabChange={(value) => setActiveTab(value as TransportationTab)}
      tabs={[
        {
          key: 'details',
          label: t('modals.tabDetails'),
          hasError: hasDetailsError,
          content: (
            <>
              {/* General Information Card */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('modals.generalInformation')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* First row: Title and Link */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative">
                      <Input
                        id="title"
                        value={formData.title || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        placeholder={t('modals.transportationName')}
                        hasError={errors.title}
                        readOnly={readOnly}
                      />
                    </div>
                    <div className="relative">
                      <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                      <Input
                        id="link"
                        value={formData.link || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                        placeholder={t('modals.transportationLink')}
                        className="pl-10"
                        readOnly={readOnly}
                      />
                    </div>
                  </div>

                  {/* Second row: Transport Type and Company */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Select
                        value={formData.transport_type || 'train'}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, transport_type: value as TransportationActivity['transport_type'] }))}
                        disabled={readOnly}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('modals.selectTransportType')} />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(transportTypeKeys).map(([value, key]) => (
                            <SelectItem key={value} value={value}>
                              {t(key as any)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                      <Input
                        id="company"
                        value={formData.company || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                        placeholder={t('modals.company')}
                        className="pl-10"
                        hasError={errors.company}
                        readOnly={readOnly}
                      />
                    </div>
                  </div>

                  {/* Travels Section */}
                  <div className="space-y-4">
                    <h4 className="text-gray-700">{t('modals.travelRoute')}</h4>
                    <div className="space-y-4">
                      {formData.travels?.map((travel, index) => (
                        <div key={index}>
                          <div className="flex flex-wrap items-center gap-4">
                            <div className="w-20 font-medium">
                              {index === 0
                                ? t('common.from')
                                : index === formData.travels!.length - 1
                                  ? t('common.to')
                                  : t('modals.stopLabel')}
                            </div>
                            <div className="flex-1">
                              <div className="relative">
                                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                                <Input
                                  value={travel.address}
                                  onChange={(e) => updateTravel(index, 'address', e.target.value)}
                                  placeholder={
                                    index === 0
                                      ? t('modals.departureLocation')
                                      : index === formData.travels!.length - 1
                                        ? t('modals.arrivalLocation')
                                        : t('modals.stopLocation')
                                  }
                                  className="pl-10"
                                  hasError={errors[`travel_${index}_address`]}
                                  readOnly={readOnly}
                                />
                              </div>
                            </div>
                            <div className="w-52">
                              <DayField
                                axis={axis}
                                date={travel.date}
                                dayIndex={travel.dayIndex}
                                onDateChange={(date) => updateTravel(index, 'date', date)}
                                onDayIndexChange={(dayIndex) => updateTravel(index, 'dayIndex', dayIndex)}
                                placeholder={t('modals.date')}
                                hasError={errors[`travel_${index}_date`]}
                                minDate={itineraryStartDate || undefined}
                                maxDate={itineraryEndDate || undefined}
                                disabled={readOnly}
                              />
                            </div>
                            <div className="w-30">
                              <Input
                                type="time"
                                value={travel.time}
                                onChange={(e) => updateTravel(index, 'time', e.target.value)}
                                placeholder="HH:MM"
                                hasError={errors[`travel_${index}_time`]}
                                readOnly={readOnly}
                              />
                            </div>
                            <div className="flex items-center justify-end">
                              {!readOnly && formData.travels && formData.travels.length > 2 && index !== 0 && index !== formData.travels.length - 1 && (
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => removeTravel(index)}
                                  className="p-3 mr-3"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add Stop Button */}
                    {!readOnly && (
                      <div className="flex justify-center pt-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => addTravel(formData.travels!.length - 2)}
                          className="w-fit"
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          {t('modals.addStop')}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Price Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{t('modals.price')}</CardTitle>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{t('modals.includePrice')}</span>
                      <Switch
                        checked={formData.include_price || false}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
                        disabled={readOnly || pricingLocked}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-center gap-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                        <Input
                          id="cost"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.cost || 0}
                          onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                          placeholder={t('modals.amountOnlyVisibleToYou')}
                          className="pl-10"
                          hasError={errors.cost}
                          readOnly={readOnly || pricingLocked}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Note Card */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('modals.note')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <RichTextEditor
                    value={formData.description || ''}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    placeholder={t('modals.shareClientPreferencesTransport')}
                    readOnly={readOnly}
                  />
                </CardContent>
              </Card>
            </>
          ),
        },
        {
          key: 'documents',
          label: t('modals.tabDocuments'),
          content: (
            <Card>
              <CardHeader>
                <CardTitle>{t('modals.documentation')}</CardTitle>
              </CardHeader>
              <CardContent>
                <UploadMultiFiles
                  files={formData.documentation || []}
                  onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                  label={t('modals.uploadBoardingPass')}
                  maxFiles={5}
                  itineraryId={itineraryId}
                  activityId={activityId}
                  readOnly={readOnly}
                />
              </CardContent>
            </Card>
          ),
        },
      ]}
    />
  );
};

export default TransportationModal;
